/**
 * Offset grid generator: the shared case generator in its row-major mode (taxonomy §3). One
 * column of full-width shelves housed into full-height sides; each row carries its own list
 * of short dividers, housed into the shelf above and below (dowelled only behind a full back).
 * Per-cell backs are small panels fitted at the back of chosen cells.
 */
import type { BuildModel, Design, Issue } from '../../domain/schemas';
import { parseConstructionProfile, type ConstructionProfile } from '../../domain/profiles';
import { fmtMm } from '../../domain/units';
import { generateCase } from '../case';
import { assembleBuild, cellsFromCentres, evenSplit, familyJoints, familyStock, fracTicks, fromCase, layable, minHeightFix, sizeErrors, splitByWeights, THIN_STOCK_ID, type FamilyBoard, type FamilyJoint } from '../build-common';
import { resolveStock, STOCKS, type AnyStock } from '../stock';
import jointsJson from './joints.json' with { type: 'json' };
import { MAX_DIVIDERS, MAX_ROWS, OFFSET_ID, OFFSET_LABEL, OFFSET_VERSION, offsetParamsOf } from './design';
import { baseErrors, baseOf, feetHardware, feetSupports, footKindFor, liftOf, slatErrors, slatsOf } from '../base';
import type { Hardware } from '../assembly';

const parsed = parseConstructionProfile(jointsJson);
if (!parsed.ok) throw new Error(`offset-grid joints.json is invalid: ${parsed.errors.join('; ')}`);
export const OFFSET_JOINTS: ConstructionProfile = Object.freeze(parsed.value) as ConstructionProfile;

/** Narrowest cell a divider may leave (ticks). */
export const MIN_CELL = 1000;

export function generateOffset(design: Design, stock?: AnyStock | null, construction?: ConstructionProfile): BuildModel {
  const c = familyJoints(OFFSET_ID, construction, OFFSET_JOINTS);
  const S = familyStock(design, stock, STOCKS, 'birch-ply-18');
  const R = resolveStock(S);
  const t = R.thicknessTicks;
  const p = offsetParamsOf(design);
  const { W, H, D } = design;
  const errors: Issue[] = sizeErrors(design, c);
  const warnings: Issue[] = [];
  const maxSpan = Math.round((c.limits?.maxSpanMm ?? c.warnings.maxShelfSpanMm) * 10);
  if (!Number.isInteger(p.rows) || p.rows < 1 || p.rows > MAX_ROWS) errors.push({ field: 'rows', message: `Rows must be between 1 and ${MAX_ROWS}.` });
  if (!Array.isArray(p.rowDividerXs) || p.rowDividerXs.length !== p.rows) errors.push({ field: 'rowDividerXs', message: `${p.rows} rows need ${p.rows} divider lists; ${p.rowDividerXs?.length ?? 0} are set.` });
  if (p.rowWeights && (p.rowWeights.length !== p.rows || p.rowWeights.some((w) => !(w > 0)))) errors.push({ field: 'rowWeights', message: `Row heights need ${p.rows} positive values.` });
  const foot = footKindFor(R.kind);
  const base = baseOf(p, foot);
  // FND-23: a thick solid frame (38 mm boards and up) has mitred corners and through-tenoned shelves
  const thick = R.kind === 'board' && t >= 380;
  const lift = liftOf(base);
  const slats = slatsOf(p);
  if (!(p.plinth >= 0 && p.plinth <= 1500)) errors.push({ field: 'plinth', message: 'The plinth can be at most 150 mm high.', fix: { label: 'Use a 150 mm plinth', patch: { plinth: 1500 } } });
  else if (p.base !== undefined) errors.push(...baseErrors(base, D, foot));
  errors.push(...slatErrors(slats, D));
  if (slats && p.back === 'rebated') errors.push({ field: 'back', message: 'Slatted sides cannot carry a rebated back; use an overlay back or none.', fix: { label: 'Use an overlay back', patch: { back: 'overlay' } } });
  const hardware: Hardware[] = [];
  let boards: FamilyBoard[] = [];
  let joints: FamilyJoint[] = [];
  let openings: { id: string; label: string; x0: number; x1: number; y0: number; y1: number }[] = [];
  let datums: { partId: string; label: string; ticks: number }[] = [];
  // the layout needs only a well-formed block and positive sizes: rules that fail never empty the model
  const shapeOk = layable(design) && Number.isInteger(p.rows) && p.rows >= 1 && Array.isArray(p.rowDividerXs) && p.rowDividerXs.length === p.rows
    && (!p.rowWeights || (p.rowWeights.length === p.rows && p.rowWeights.every((w) => w > 0)));
  if (shapeOk) {
    const A = H - lift - 2 * t - (p.rows - 1) * t;
    const clears = p.rowWeights ? splitByWeights(A, p.rowWeights) : evenSplit(A, p.rows);
    const minO = c.envelope.minOpeningTicks;
    const thinT = resolveStock(STOCKS[THIN_STOCK_ID]!).thicknessTicks;
    const rowFix = minHeightFix({
      count: p.rows, minCount: 1, key: 'rows', noun: 'row', t, thinT, min: minO, currentStock: S.id,
      clearWith: (n, tt) => { const a = H - lift - 2 * tt - (n - 1) * tt; return p.rowWeights && n === p.rows ? Math.min(...splitByWeights(a, p.rowWeights)) : Math.floor(a / n); },
    });
    clears.forEach((g, r) => { if (g < minO) errors.push({ field: `row-${r}`, message: `Row ${r + 1} would be ${fmtMm(g)} mm high; the minimum clear height is ${fmtMm(minO)} mm.`, ...(rowFix ? { fix: rowFix } : {}) }); });
    const rowCells = p.rowDividerXs.map((xs, r) => {
      if (xs.length > MAX_DIVIDERS) errors.push({ field: `row-${r}`, message: `Row ${r + 1} has ${xs.length} dividers; at most ${MAX_DIVIDERS}.` });
      const centres = xs.slice().sort((a, b) => a - b).map((x) => fracTicks(W, x));
      const cells = cellsFromCentres(t, W - t, centres, t);
      cells.forEach((w, k) => {
        if (w < MIN_CELL) errors.push({ field: `row-${r}`, message: `Row ${r + 1}, cell ${k + 1} would be ${fmtMm(w)} mm wide; the narrowest is ${fmtMm(MIN_CELL)} mm.` });
        else if (w > maxSpan) {
          // fix: a divider in the middle of that cell (when the row has room for one more)
          const sorted = xs.slice().sort((a, b) => a - b);
          const edges = [0, ...sorted, 1];
          const mid = Math.round(((edges[k]! + edges[k + 1]!) / 2) * 1000) / 1000;
          const rowDividerXs = p.rowDividerXs.map((row, i) => (i === r ? [...sorted, mid].sort((a, b) => a - b) : row.slice()));
          const fix = sorted.length < MAX_DIVIDERS ? { label: `Add a divider to row ${r + 1}`, patch: { pattern: 'free', rowDividerXs } } : undefined;
          warnings.push({ field: `row-${r}`, message: `Row ${r + 1}, cell ${k + 1} spans ${fmtMm(w)} mm. Under a full load of books the shelf may sag; add a divider so no shelf spans more than ${fmtMm(maxSpan)} mm.`, ...(fix ? { fix } : {}) });
        }
      });
      return cells;
    });
    {
      // rows or cells that leave no room still lay out at 1 mm so the parts show beside the error
      clears.forEach((g, r) => { clears[r] = Math.max(g, 10); });
      rowCells.forEach((cs) => cs.forEach((w, k) => { cs[k] = Math.max(w, 10); }));
      const openBack = p.back === 'none' || p.back === 'per-cell';
      const caseBack = p.back === 'per-cell' ? 'none' : p.back;
      const bz = p.back === 'per-cell' ? R.back.thicknessTicks : 0;
      const out = generateCase({
        W, H, D, t, plinth: p.plinth, base, slats, corner: thick ? 'mitre' : 'butt', back: { kind: caseBack, thickness: caseBack === 'none' ? 0 : R.back.thicknessTicks },
        shelfJoint: 'housed-dado', rowDividerJoint: openBack ? 'housed-dado' : 'dowel-butt',
        columns: [{ clear: W - 2 * t, rows: clears.map((g, r) => ({ clear: g, cells: rowCells[r]! })) }],
        naming: {
          shelf: (_c, k) => ({ id: `shelf-${k}`, label: `Shelf ${k}` }),
          divider: (k) => ({ id: `divider-${k}`, label: `Divider ${k}` }),
          rowDivider: (_c, r, k) => ({ id: `div-${r + 1}-${k}`, label: `Row ${r + 1} divider ${k}` }),
          back: (k, n) => (n === 1 ? { id: 'back', label: 'Back' } : { id: `back-${k}`, label: `Back ${k} of ${n}` }),
          opening: (_c, r, cell) => ({ id: `cell-${r + 1}-${cell + 1}`, label: `Row ${r + 1}, cell ${cell + 1}` }),
        },
      });
      const f = fromCase(out);
      boards = f.boards; joints = f.joints;
      if (base.kind === 'feet') hardware.push(...feetHardware(lift, feetSupports(out.boards), foot));
      if (caseBack !== 'none') {
        const bk = boards.find((q) => q.role === 'back');
        const sheet = R.back.sheet;
        if (bk && !(Math.max(bk.l, bk.w) <= sheet.lengthTicks - 2 * c.edgeAllowanceTicks && Math.min(bk.l, bk.w) <= sheet.widthTicks - 2 * c.edgeAllowanceTicks)) {
          errors.push({ field: 'back', message: `The back (${fmtMm(bk.box.sx)} × ${fmtMm(bk.box.sy)} mm) is larger than one sheet; choose per-cell backs or none.` });
        }
      }
      if (p.back === 'per-cell') {
        // a small back fitted at the back of each chosen cell, screwed to the shelf below
        for (const o of out.openings) {
          if (!p.cellBacks.includes(`${o.row}-${o.cell}`)) continue;
          const below = o.row === 0 ? 'bottom' : `shelf-${o.row}`;
          const id = `cellback-${o.row + 1}-${o.cell + 1}`;
          const w = o.x1 - o.x0, h = o.y1 - o.y0;
          const vertical = h >= w;
          boards.push({ id, role: 'back', label: `Back, row ${o.row + 1} cell ${o.cell + 1}`, l: vertical ? h : w, w: vertical ? w : h, t: bz, grainWorld: vertical ? 'y' : 'x', box: { x: o.x0, y: o.y0, z: 0, sx: w, sy: h, sz: bz }, stock: 'back' });
          joints.push({ a: id, b: below, kind: 'screwed-butt', face: 'back', datum: o.y0 });
        }
      }
      openings = out.openings.map((o) => ({ id: o.id, label: o.label, x0: o.x0, x1: o.x1, y0: o.y0, y1: o.y1 }));
      datums = out.datums.map((d) => ({ partId: d.partId, label: d.label, ticks: d.ticks }));
    }
  }
  if (H > Math.round(c.warnings.antiTipAboveMm * 10)) warnings.push({ field: 'H', message: `At ${fmtMm(H)} mm tall this must be fixed to the wall (anti-tip).` });
  return assembleBuild({
    familyId: OFFSET_ID, familyVersion: OFFSET_VERSION, familyLabel: OFFSET_LABEL, design, stock: S, construction: c,
    boards, joints, openings, datums, errors, warnings, hardware, clearWidth: W - 2 * t, shelves: Math.max(0, p.rows - 1),
  });
}
